import React, { useState, useEffect, useCallback } from 'react';
import { NodeProps } from '@xyflow/react';
import useLocale from 'next-translate/useTranslation';
import { BaseNode, createInputHandle, createOutputHandle } from './BaseNode';
import { useWorkflowStore } from '@/lib/workflows/store';
import { NodeExecutionStatus } from 'types';

import { TipTapPromptEditor } from '../TipTapPromptEditor';
import Button from '@/components/buttons/Button';
import { PlayIcon, RefreshIcon } from '@heroicons/react/outline';
import { NodeActionsDropdown } from './NodeActionsDropdown';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface MusicGenerationConfig {
  prompt: string;
  isCustomMode: boolean;
  songStyle: string;
  songLyrics: string;
  isInstrumental: boolean;
  vocalGender: 'm' | 'f' | '';
}

interface MusicGenerationNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
}

export function MusicGenerationNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
}: MusicGenerationNodeProps) {
  const { t } = useLocale('common');
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const allNodes = useWorkflowStore((state) => state.nodes);
  const allEdges = useWorkflowStore((state) => state.edges);

  const [showSettings, setShowSettings] = useState<boolean>(
    !(typeof (data as { result?: unknown })?.result === 'string'),
  );

  const executionStatus = ((s) => {
    return typeof s === 'string' &&
      ['idle', 'running', 'success', 'error'].includes(s)
      ? (s as NodeExecutionStatus)
      : undefined;
  })((data as { status?: unknown })?.status);

  const isRunning = executionStatus === 'running';

  const config: MusicGenerationConfig =
    (data.config as MusicGenerationConfig) || {
      prompt: '',
      isCustomMode: false,
      songStyle: '',
      songLyrics: '',
      isInstrumental: false,
      vocalGender: '',
    };

  useEffect(() => {
    if (
      typeof (data as { result?: unknown })?.result === 'string' &&
      showSettings
    ) {
      setShowSettings(false);
    }
  }, [data.result]);

  const shouldShowResult =
    typeof (data as { result?: unknown })?.result === 'string' && !isRunning;

  const updateConfig = (updates: Partial<MusicGenerationConfig>) => {
    updateNodeData(id, {
      config: {
        ...config,
        ...updates,
      },
    });
  };

  const handlePromptChange = (value: string) => {
    updateConfig({ prompt: value.slice(0, 500) });
  };

  const rawError = (data as any)?.error;
  const errorText =
    typeof rawError === 'string'
      ? rawError.trim()
      : rawError != null
        ? String(rawError).trim()
        : '';

  const getConnectedTextNodes = useCallback(() => {
    const connectedEdges = allEdges.filter(
      (edge) =>
        edge.target === id &&
        (edge.targetHandle === 'prompt' || edge.targetHandle === 'text'),
    );

    return connectedEdges
      .map((edge) => {
        const sourceNode = allNodes.find((n) => n.id === edge.source);
        return {
          id: sourceNode?.id || '',
          label: (sourceNode?.data?.label as string) || 'Node',
        };
      })
      .filter((n) => n.id);
  }, [id, allNodes, allEdges]);

  return (
    <BaseNode
      title={
        (typeof (data as { label?: unknown })?.label === 'string' &&
          (data as { label?: string }).label) ||
        t('Music Generation')
      }
      icon={
        <svg
          className='w-4 h-4 text-amber-600'
          fill='none'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3' />
        </svg>
      }
      hasInput={false}
      hasOutput={false}
      status={executionStatus}
      selected={selected}
      nodeId={nodeId || id}
      onRunNode={onRunNode}
      customInputs={createInputHandle('text', 'text', 50)}
      customOutputs={createOutputHandle('audio', 'audio', 50)}
      hideToolbarActions
      customHeaderButtons={
        <>
          {data.result && (
            <SimpleTooltip
              title={showSettings ? t('Hide settings') : t('Show settings')}
              side='top'
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSettings(!showSettings);
                }}
                className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
              >
                <svg
                  className='w-5 h-5 text-gray-600 dark:text-gray-300'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                  <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                </svg>
              </button>
            </SimpleTooltip>
          )}
          <NodeActionsDropdown
            nodeId={nodeId || id}
            nodeTitle={(data.label as string) || t('Music Generation')}
            mediaUrl={data.result as string}
            mediaType='audio'
            filename='generated-music.mp3'
          />
        </>
      }
    >
      <div className='w-[400px]'>
        {/* Collapsible Settings Section */}
        <div
          className={`space-y-3 px-4 pt-3 pb-3 border-b border-gray-200 dark:border-gray-700 ${
            showSettings ? '' : 'hidden'
          }`}
        >
          {/* Custom Mode Toggle */}
          <div className='flex items-center justify-between'>
            <label className='block text-xs font-medium text-gray-700 dark:text-gray-300'>
              {t('Custom Mode')}
              <SimpleTooltip
                title={t(
                  'Switch to custom mode to specify style and lyrics separately',
                )}
              >
                <span className='ml-1 text-gray-400 cursor-help'>?</span>
              </SimpleTooltip>
            </label>
            <button
              onClick={(e) => {
                e.stopPropagation();
                updateConfig({ isCustomMode: !config.isCustomMode });
              }}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                config.isCustomMode
                  ? 'bg-primary-600'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  config.isCustomMode
                    ? 'translate-x-[18px]'
                    : 'translate-x-[2px]'
                }`}
              />
            </button>
          </div>

          {config.isCustomMode ? (
            <>
              {/* Style Input */}
              <div>
                <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('Style')}
                  <SimpleTooltip
                    title={t(
                      'Describe the musical style, genre, mood, and instruments',
                    )}
                  >
                    <span className='ml-1 text-gray-400 cursor-help'>?</span>
                  </SimpleTooltip>
                </label>
                <input
                  type='text'
                  value={config.songStyle || ''}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateConfig({ songStyle: e.target.value.slice(0, 200) });
                  }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder={t('e.g., Upbeat pop rock with electric guitar')}
                  maxLength={200}
                  className='w-full text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500'
                />
                <p className='text-[10px] text-gray-400 mt-0.5 text-right'>
                  {(config.songStyle || '').length}/200
                </p>
              </div>

              {/* Lyrics Input */}
              <div>
                <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  {t('Lyrics')}
                  <SimpleTooltip title={t('Provide the lyrics for your song')}>
                    <span className='ml-1 text-gray-400 cursor-help'>?</span>
                  </SimpleTooltip>
                </label>
                <textarea
                  rows={4}
                  value={config.songLyrics || ''}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateConfig({ songLyrics: e.target.value.slice(0, 3000) });
                  }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder={t(
                    'Enter lyrics with verse, chorus, bridge sections...',
                  )}
                  maxLength={3000}
                  className='w-full text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500 resize-y'
                />
                <p className='text-[10px] text-gray-400 mt-0.5 text-right'>
                  {(config.songLyrics || '').length}/3000
                </p>
              </div>
            </>
          ) : (
            /* Song Description */
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Song Description (optional if connected)')}
              </label>
              <TipTapPromptEditor
                value={config.prompt || ''}
                onChange={handlePromptChange}
                connectedNodes={getConnectedTextNodes()}
                placeholder={t(
                  'Describe your song... Type @ to reference connected nodes',
                )}
              />
              <div className='flex items-center justify-between mt-1'>
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                  {t('Or connect text from another node')}
                </p>
                <p className='text-[10px] text-gray-400'>
                  {(config.prompt || '').length}/500
                </p>
              </div>
            </div>
          )}

          {/* Instrumental Toggle */}
          <div className='flex items-center justify-between'>
            <label className='block text-xs font-medium text-gray-700 dark:text-gray-300'>
              {t('Instrumental Only')}
              <SimpleTooltip title={t('Generate music without vocals/lyrics')}>
                <span className='ml-1 text-gray-400 cursor-help'>?</span>
              </SimpleTooltip>
            </label>
            <button
              onClick={(e) => {
                e.stopPropagation();
                updateConfig({ isInstrumental: !config.isInstrumental });
              }}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                config.isInstrumental
                  ? 'bg-primary-600'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  config.isInstrumental
                    ? 'translate-x-[18px]'
                    : 'translate-x-[2px]'
                }`}
              />
            </button>
          </div>

          {/* Vocal Gender - only when not instrumental */}
          {!config.isInstrumental && (
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Vocal Gender')}
                <SimpleTooltip
                  title={t('Select the preferred gender for the vocals')}
                >
                  <span className='ml-1 text-gray-400 cursor-help'>?</span>
                </SimpleTooltip>
              </label>
              <select
                value={config.vocalGender || ''}
                onChange={(e) => {
                  e.stopPropagation();
                  updateConfig({
                    vocalGender: e.target.value as 'm' | 'f' | '',
                  });
                }}
                onClick={(e) => e.stopPropagation()}
                className='w-full text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500'
              >
                <option value=''>{t('Auto (Let AI decide)')}</option>
                <option value='m'>{t('Male')}</option>
                <option value='f'>{t('Female')}</option>
              </select>
            </div>
          )}

          {/* Run Button */}
          <div className='pt-1'>
            <Button
              className='w-full justify-center'
              variant='outline'
              isLoading={isRunning}
              onClick={(e) => {
                e.stopPropagation();
                onRunNode?.(nodeId || id);
              }}
            >
              {data.result ? (
                <>
                  <RefreshIcon className='w-4 h-4 mr-2' />
                  <span>{t('Regenerate')}</span>
                </>
              ) : (
                <>
                  <PlayIcon className='w-4 h-4 mr-2' />
                  <span>{t('Run')}</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Result Preview - Audio Player */}
        {shouldShowResult && (
          <div className='p-3'>
            <div className='bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4'>
              <audio src={data.result as string} controls className='w-full' />
            </div>
          </div>
        )}

        {/* Error Display */}
        {errorText && (
          <div className='mx-3 mb-3 border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2 py-2 rounded'>
            <p className='text-xs text-red-600 dark:text-red-400'>
              {errorText}
            </p>
          </div>
        )}
      </div>
    </BaseNode>
  );
}
